import { takeEvery, takeLatest, call, put, debounce, select, all, throttle, takeLeading } from 'redux-saga/effects';
import { path, pipe, head, propOr, omit, values } from 'ramda';

import { withAlert } from 'store/alerts';
import api from 'api';

import { ID, FILES, EXPAND, SUB_PATIENT, SUB_DRUG, LIMIT } from '.';
import { deleteMedication, updateMedicalHistory, updateMedication, updateMedications } from './actions';
import { getMedicalHistoryProp, getMedications } from './selectors';
import {
  FETCH_MEDICAL_HISTORY,
  FETCH_COLLECT_MEDICAL_HISTORY,
  SAVE_MEDICAL_HISTORY,
  UPLOAD_MEDIA_DATA,
  FETCH_SEARCHABLE_MEDIA,
  DELETE_SEARCHABLE_MEDIA,
  FETCH_DRUGS,
  SAVE_DRUG,
  CREATE_MEDICATION,
  SAVE_MEDICATION,
  REMOVE_MEDICATION,
} from './types';

const query = { [EXPAND]: ['files'] };

function* createMedicalHistory({ payload }) {
  const data = yield call(api.postMedicalHistory, { [SUB_PATIENT]: payload });
  yield put(updateMedicalHistory(data));
}

function* fetchMedicalHistory({ payload }) {
  const [{ data }, { data: medications }] = yield all([
    call(api.getMedicalHistory, { [SUB_PATIENT]: payload, ...query }),
    call(api.getPatientMedications, { [SUB_PATIENT]: payload, [LIMIT]: 100, [EXPAND]: [SUB_DRUG] }),
  ]);

  if (path([0, ID], data)) {
    yield pipe(head, updateMedicalHistory, put)(data);
  } else {
    yield createMedicalHistory({ payload });
  }

  yield put(updateMedications(medications));
}

function* fetchCollectMedicalHistory({ payload }) {
  const { data } = yield call(api.getMedicalHistory, payload);
  return { success: data && data[0] };
}

function* saveMedicalHistory({ payload }) {
  const medicalHistoryId = yield select(getMedicalHistoryProp(ID));
  const data = yield call(
    api.patchMedicalHistory,
    {
      ...payload,
      medicalHistoryId,
      ...(payload[FILES] ? { [FILES]: payload[FILES].map((file) => propOr(file, ID, file)) } : {}),
    },
    query
  );
  yield put(updateMedicalHistory(data));
}

function* uploadMediaData({ payload }) {
  const { files, patientId } = payload;
  const media = yield select(getMedicalHistoryProp(FILES)) || [];
  const filesData = yield all(files.map((file) => call(api.uploadGCloudFile, file, 'indexed')));
  const data = yield all(filesData.map((fileData) => call(api.uploadSearchableMedia, fileData, patientId)));

  return { success: [...media, ...data] };
}

function* fetchSearchableMedia({ payload }) {
  const { id, search } = payload;

  const success = yield call(api.getSearchableMedia, id, { query: search });

  return { success };
}

function* deleteSearchableMedia({ payload }) {
  const { id, fileData } = payload;

  yield call(api.deleteSearchableMedia, id);
  yield call(api.deleteFileFromGCloud, fileData);

  return { success: id };
}

function* fetchDrugs({ payload }) {
  return { success: yield call(api.getDrugs, payload) };
}

function* saveDrug({ payload }) {
  const medications = yield select(getMedications);
  const data = yield call(api.patchDrug, payload[ID], omit([ID], payload));
  let targetMedication;

  values(medications).some((medication) => {
    if (medication[SUB_DRUG]?.[ID] === data[ID]) {
      targetMedication = medication;
      return true;
    }
    return false;
  });

  if (targetMedication) yield put(updateMedication({ ...targetMedication, [SUB_DRUG]: data }));
}

function* createMedication({ payload }) {
  const data = yield call(api.postMedication, payload, { [EXPAND]: [SUB_DRUG] });

  yield put(updateMedication(data));
}

function* saveMedication({ payload }) {
  const data = yield call(api.patchMedication, payload[ID], omit([ID], payload), { [EXPAND]: [SUB_DRUG] });

  yield put(updateMedication(data));
}

function* removeMedication({ payload }) {
  const data = yield call(api.deleteMedication, payload);

  yield put(deleteMedication(data));
}

export default function* watchMedicalHistory() {
  yield takeLatest(FETCH_MEDICAL_HISTORY, withAlert(fetchMedicalHistory));
  yield takeEvery(FETCH_COLLECT_MEDICAL_HISTORY, withAlert(fetchCollectMedicalHistory));
  yield debounce(500, SAVE_MEDICAL_HISTORY, withAlert(saveMedicalHistory));
  yield takeEvery(UPLOAD_MEDIA_DATA, withAlert(uploadMediaData));
  yield debounce(500, FETCH_SEARCHABLE_MEDIA, withAlert(fetchSearchableMedia));
  yield takeEvery(DELETE_SEARCHABLE_MEDIA, withAlert(deleteSearchableMedia));
  yield throttle(500, FETCH_DRUGS, withAlert(fetchDrugs));
  yield takeLeading(CREATE_MEDICATION, withAlert(createMedication));
  yield takeEvery(SAVE_DRUG, withAlert(saveDrug));
  yield throttle(500, SAVE_MEDICATION, withAlert(saveMedication));
  yield takeLeading(REMOVE_MEDICATION, withAlert(removeMedication));
}
